import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Stack, Typography, Avatar, Menu, MenuItem, Divider, IconButton, Tooltip,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useAuth } from 'contexts/AuthContext';
import { ColorModeToggle } from 'dashboard/components/common';

const TITLES = {
  overview: 'Overview',
  users: 'Users',
  brands: 'Brands',
  subscriptions: 'Subscriptions',
  earnings: 'Earnings',
  domains: 'Domains',
  errors: 'Errors (last 7d)',
  audit: 'Audit log',
};

export default function AdminTopbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [anchor, setAnchor] = useState(null);

  const section = pathname.replace(/^\/admin\/?/, '').split('/')[0] || 'overview';
  const title = TITLES[section] || 'Platform admin';

  const email = user?.email || user?.email_id || '';
  const initial = (email[0] || 'A').toUpperCase();

  const signOut = () => {
    // Drop the brand-as-admin opt-in too so the next sign-in starts
    // clean — no stale flag steering admin back to brand.
    try { sessionStorage.removeItem('tpo_brand_as_admin'); } catch (_) { /* ignore */ }
    logout();
    navigate('/', { replace: true });
  };

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1100,
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        px: '28px',
        py: '12px',
      }}
    >
      <Stack direction='row' alignItems='center' spacing='16px'>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: '11px', color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Platform admin
          </Typography>
          <Typography sx={{ fontSize: '20px', fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
            {title}
          </Typography>
        </Box>

        <Tooltip title='Open brand dashboard (your own brands, if any)'>
          <IconButton
            size='small'
            onClick={() => {
              // Set the opt-in flag so the brand AdminLayout's
              // is-admin safety net DOESN'T bounce us straight back
              // here. Cleared on sign-out / browser close.
              try { sessionStorage.setItem('tpo_brand_as_admin', '1'); } catch (_) { /* private mode */ }
              navigate('/dashboard/brand/overview');
            }}
          >
            <OpenInNewIcon fontSize='small' />
          </IconButton>
        </Tooltip>

        {/* Same switch as the brand dashboard, in the same place relative to
            the avatar. The platform admin is a separate layout with its own
            top bar, which is how it ended up as the one screen with no way to
            change mode. */}
        <ColorModeToggle />

        <Stack
          direction='row'
          alignItems='center'
          spacing='10px'
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{ cursor: 'pointer', px: '8px', py: '4px', borderRadius: '8px', '&:hover': { bgcolor: 'action.hover' } }}
        >
          <Avatar sx={{ width: 30, height: 30, bgcolor: '#7C3AED', fontSize: '13px', fontWeight: 700 }}>
            {initial}
          </Avatar>
          <Box sx={{ textAlign: 'left' }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'text.primary', lineHeight: 1.1 }}>
              {email || 'admin'}
            </Typography>
            <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>Platform admin</Typography>
          </Box>
        </Stack>

        <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
          <MenuItem disabled sx={{ opacity: 1 }}>
            <Box>
              <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>Signed in as</Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'text.primary' }}>{email}</Typography>
            </Box>
          </MenuItem>
          <Divider />
          <MenuItem onClick={signOut}>
            <LogoutIcon fontSize='small' sx={{ mr: '8px' }} />
            Sign out
          </MenuItem>
        </Menu>
      </Stack>
    </Box>
  );
}
